import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { Button } from "@shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Copy, Edit, Eye, MoreVertical } from "lucide-react";
import { useState } from "react";

interface TemplatesListProps {
  templateList: any;
  viewMode: "grid" | "list";
  onToggleStatus: (id: string, isActive: boolean) => void;
  onEdit: (id: string, templateId: string) => void;
  onView: (id: string) => void;
  onClone: (id: string) => void;
}

export const TemplatesList = ({
  templateList,
  viewMode,
  onToggleStatus,
  onEdit,
  onView,
  onClone,
}: TemplatesListProps) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const columns = [
    {
      key: "configuredTemplateName",
      header: "Name",
      render: (value: any, row: any) => {
        return <div className="font-medium">{row.configuredTemplateName}</div>;
      },
    },
    {
      key: "createdUserRemoteName",
      header: "Created By	",
    },
    {
      key: "createdUserRemoteName",
      header: "Created On",
    },
    {
      key: "usageCount",
      header: "Usage Count",
    },
    {
      key: "isActive",
      header: "Status",
      render: (value: any, row: any) => {
        return (
          <Switch
            checked={row.isActive}
            onCheckedChange={(checked) => {
              onToggleStatus(row.id, checked);
              logEvent(`BX Template Activation Status Updated - ${row.id}`, {
                status: checked,
              });
            }}
          />
        );
      },
    },
  ];

  const actions = [
    {
      label: "View",
      variant: "outline" as const,
      onClick: (row: any, index: number) => {
        onView(row.id);
      },
    },
    {
      label: "Edit",
      variant: "outline" as const,
      onClick: (row: any, index: number) => {
        onEdit(row.baseTemplate, row.id);
      },
    },
    {
      label: "Clone",
      variant: "outline" as const,
      onClick: (row: any, index: number) => {
        onClone(row.id);
        logEvent("BX Template Cloned", {
          templateId: row.id,
        });
      },
    },
  ];

  const [currentPage, setTemplateListCurrentPage] = useState(1);

  return (
    <>
      {viewMode === "list" ? (
        <div className="bg-white rounded-md border shadow-sm overflow-hidden">
          <GenericTable
            data={templateList?.result ?? []}
            columns={columns}
            actions={actions}
            rowKey="id"
            emptyMessage="No Templates found"
            loading={false}
            onPageChange={() => {
              setTemplateListCurrentPage;
            }}
            pagination={{
              currentPage: currentPage,
              totalPages: Math.ceil((templateList?.count || 0) / PAGE_SIZE),
              totalItems: templateList?.count || 0,
              itemsPerPage: PAGE_SIZE,
              startItem: (currentPage - 1) * PAGE_SIZE + 1,
              endItem: Math.min(
                currentPage * PAGE_SIZE,
                templateList?.count || 0
              ),
            }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {templateList?.result?.map((template) => (
            <div
              key={template?.id}
              className="bg-white rounded-md border shadow-sm overflow-hidden"
            >
              <div className="h-40 flex items-center justify-center bg-white border-b p-4">
                {/* Thumbnail area */}
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl mb-2">
                      {template?.configuredTemplateName?.charAt(0)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4">
                {/* Details area */}
                <div className="mb-3">
                  <h3
                    className="font-medium truncate"
                    title={template?.configuredTemplateName}
                  >
                    {template?.configuredTemplateName}
                  </h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created by:</span>
                    <span>{template?.createdUserRemoteName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created on:</span>
                    <span>{template?.createdDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Usage count:</span>
                    <span>{template?.usageCount || 0}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={template?.isActive}
                        onCheckedChange={(checked) =>
                          onToggleStatus(template?.id, checked)
                        }
                      />
                      <span className="text-xs">
                        {template?.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onView(template?.id)}>
                          <Eye className="h-4 w-4 mr-2" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            onEdit(template?.baseTemplate, template?.id)
                          }
                        >
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onClone(template?.id)}>
                          <Copy className="h-4 w-4 mr-2" /> Clone
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the template. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteId && handleDelete(deleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> */}
    </>
  );
};
